// Centralized Supabase client to avoid multiple GoTrueClient instances
import { createClient } from '@supabase/supabase-js';
import { createResilientFetch } from '@tradesmatepro/shared-ui/utils/resilientFetch';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './env';
import { report } from './errorReporter';

// Connection problems: time out and retry (PRODUCT_DECISIONS, 2026-10-23). Every Supabase
// call times out instead of waiting forever; reads retry twice, writes never retry. Policy
// and tiers live in packages/shared-ui/utils/resilientFetch.js; status reaches the screen
// through ConnectionBanner, and timeouts reach the runtime error log through report().
const resilientFetch = createResilientFetch({ supabaseUrl: SUPABASE_URL, report });

// Fired on window after any successful write to the per-company rows that
// services/SettingsService.js caches for the session (companies, the settings view and its
// base company_settings), or to an RPC that writes them. Detected HERE, on the transport,
// so every writer invalidates the cache — including ones added later — without each
// component having to remember to. Server-side writes (edge functions, webhooks) are
// covered by the cache's TTL and by sign-in always reading fresh.
export const COMPANY_DATA_WRITE_EVENT = 'tm:company-data-written';
const COMPANY_DATA_TABLES = /\/rest\/v1\/(companies|settings|company_settings)(\?|$)/;
// Every RPC called from this app whose body UPDATEs/INSERTs companies or company_settings
// (pg_proc bodies, verified live 2026-10-22), counters included: correctness over a refetch.
const COMPANY_DATA_RPCS = /\/rest\/v1\/rpc\/(update_onboarding_progress|generate_document_number|create_vendor|accept_dispute_resolution|safe_upsert_company_settings|update_company_profile|update_company_tax_id)(\?|$)/;

const fetchWithCompanyDataSignal = async (input, init) => {
  const res = await resilientFetch(input, init);
  try {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = String(init?.method || input?.method || 'GET').toUpperCase();
    if (res.ok && (
      (method !== 'GET' && method !== 'HEAD' && COMPANY_DATA_TABLES.test(url)) ||
      (method === 'POST' && COMPANY_DATA_RPCS.test(url))
    )) {
      window.dispatchEvent(new Event(COMPANY_DATA_WRITE_EVENT));
    }
  } catch { /* signalling must never break the request */ }
  return res;
};

// Create singleton clients
let supabaseClient = null;
let supabaseAdminClient = null;

export function getSupabaseClient() {
  const g = (typeof window !== 'undefined' ? window : {});
  if (!supabaseClient) {
    // Reuse across HMR to avoid multiple auth clients
    if (g.__tm_supabase_client) {
      supabaseClient = g.__tm_supabase_client;
    } else {
      supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          storageKey: 'tm-web-auth',
        },
        global: { fetch: fetchWithCompanyDataSignal }
      });
      g.__tm_supabase_client = supabaseClient;
    }
  }
  return supabaseClient;
}

export function getSupabaseAdminClient() {
  // Avoid creating admin client in the browser to prevent multiple auth clients and exposure
  if (process.env.NODE_ENV !== 'production') {
    console.warn('getSupabaseAdminClient called in browser. Returning user client to avoid multiple GoTrueClient instances.');
  }
  return getSupabaseClient();
}

// Export default client for convenience (keep singleton semantics)
export const supabase = getSupabaseClient();
export const supabaseAdmin = supabase;
